// COUPLING: everything here patches the SVG that Apex draws, through its
// internal class names (`g.apexcharts-bar-series`, `path.apexcharts-bar-area`,
// `.apexcharts-xaxis-label`, `.apexcharts-yaxis-annotations`,
// `.apexcharts-inner`) and the `el` on its chart instance. None of that is
// public API. Verified against apexcharts 7.6.1 (node_modules); re-check these
// selectors when upgrading it.
//
// Bars have 24px top and 4px bottom corners, lifted 6px off the x-axis line —
// bristol's expanded card (`renderComboBar` with `expanded` and `axes`).
export const TOP_RADIUS_PX = 24
const BOTTOM_RADIUS_PX = 4
const AXIS_GAP_PX = 6

/** The part of Apex's chart instance used here (its types don't cover `el`). */
export interface ApexChart {
  el: HTMLElement
}

const isApexChart = (chart: unknown): chart is ApexChart =>
  typeof chart === 'object' &&
  chart !== null &&
  'el' in chart &&
  chart.el instanceof HTMLElement

const chartElement = (chart: unknown): HTMLElement | undefined =>
  isApexChart(chart) ? chart.el : undefined

/** The data point an Apex event's options name, if they name one. */
export const dataPointIndexOf = (options: unknown): number | null =>
  typeof options === 'object' &&
  options !== null &&
  'dataPointIndex' in options &&
  typeof options.dataPointIndex === 'number'
    ? options.dataPointIndex
    : null

// Apex has no option for a gap between the bars and the axis line; moving the
// series group moves each bar together with its hover target.
export const liftBarsOffAxis = (chart: unknown) => {
  const series = chartElement(chart)?.querySelector<SVGGElement>(
    'g.apexcharts-bar-series'
  )
  if (series) series.style.transform = `translateY(-${AXIS_GAP_PX}px)`
}

// Apex rounds both ends of a bar alike, or only the top. Once drawn, each bar
// gets the design's 24px-top / 4px-bottom shape, and a clip of the same shape
// so its hover ring (a stroke, see the CSS) only shows inside the bar.
let clipSequence = 0
export const shapeBars = (chart: unknown) => {
  const svg = chartElement(chart)?.querySelector('svg')
  if (!svg) return
  const ns = 'http://www.w3.org/2000/svg'
  const defs =
    svg.querySelector('defs') ??
    svg.insertBefore(document.createElementNS(ns, 'defs'), svg.firstChild)
  svg
    .querySelectorAll<SVGPathElement>('path.apexcharts-bar-area')
    .forEach(bar => {
      const { x, y, width: w, height: h } = bar.getBBox()
      if (!(w > 0 && h > 0)) return
      const top = Math.min(TOP_RADIUS_PX, w / 2, h / 2)
      const bottom = Math.min(BOTTOM_RADIUS_PX, w / 2, h / 2)
      const d =
        `M${x + top} ${y}H${x + w - top}A${top} ${top} 0 0 1 ${x + w} ${y + top}` +
        `V${y + h - bottom}A${bottom} ${bottom} 0 0 1 ${x + w - bottom} ${y + h}` +
        `H${x + bottom}A${bottom} ${bottom} 0 0 1 ${x} ${y + h - bottom}` +
        `V${y + top}A${top} ${top} 0 0 1 ${x + top} ${y}Z`
      bar.setAttribute('d', d)
      const id = bar.dataset['clipId'] ?? `ln-bar-clip-${clipSequence++}`
      bar.dataset['clipId'] = id
      let clip = defs.querySelector(`#${id}`)
      if (!clip) {
        clip = document.createElementNS(ns, 'clipPath')
        clip.id = id
        clip.appendChild(document.createElementNS(ns, 'path'))
        defs.appendChild(clip)
      }
      clip.firstElementChild?.setAttribute('d', d)
      bar.setAttribute('clip-path', `url(#${id})`)
    })
}

// The hovered bar's x-axis label is bold, per the design. Labels are matched
// by text rather than index, since Apex may drop overlapping ones.
export const boldLabel = (chart: unknown, label: string | null) =>
  chartElement(chart)
    ?.querySelectorAll<SVGTextElement>('.apexcharts-xaxis-label')
    .forEach(text => {
      text.style.fontWeight =
        label !== null && text.textContent?.trim().startsWith(label)
          ? '500'
          : ''
    })

// Apex stops a y-axis annotation's line at the plot's inner edge; stretch it to
// the SVG's own width so the target reads as spanning the whole card. Apex
// redraws the annotation at its default length on update, so this runs on
// mounted and updated.
export const extendGoalLine = (chart: unknown) => {
  const el = chartElement(chart)
  const width =
    el?.querySelector('svg')?.viewBox.baseVal.width || el?.clientWidth
  if (!el || !width) return
  el.querySelectorAll('.apexcharts-yaxis-annotations line').forEach(line =>
    line.setAttribute('x2', String(width))
  )
}

// The target's label sits in a gap at the middle of the line (its background is
// the card's, so it cuts the line). Apex can only pin the label to an edge, so
// it is moved to the line's midpoint once drawn, after extendGoalLine.
export const centerGoalLabel = (chart: unknown) => {
  const el = chartElement(chart)
  const group = el?.querySelector('.apexcharts-yaxis-annotations')
  const line = group?.querySelector('line')
  const label = group?.querySelector('text')
  if (!el || !group || !line || !label) return
  // The plot group is translated right by the y-axis labels, so in its own
  // coordinates the line ends at x2 minus that offset.
  const offsetX =
    el
      .querySelector<SVGGElement>('.apexcharts-inner')
      ?.transform.baseVal.consolidate()?.matrix.e ?? 0
  const mid =
    (parseFloat(line.getAttribute('x1') ?? '0') +
      parseFloat(line.getAttribute('x2') ?? '0') -
      offsetX) /
    2
  label.setAttribute('text-anchor', 'middle')
  label.setAttribute('x', String(mid))
  const box = group.querySelector('rect')
  if (box) {
    const width = parseFloat(box.getAttribute('width') ?? '0')
    box.setAttribute('x', String(mid - width / 2))
  }
}
